import { useState, useEffect, useCallback } from 'react';
import { getDefaultObject, getRecord, searchEntityRecords } from '../../../utils/apiUtils';
import { ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE, OPERATOR_AND, OPERATOR_EQUAL } from '../../../dao/dbConstants';
import { showErrorMessage } from '@/shared/components/toast-system';
import { SERVER_URL } from '../../../utils/apiUtilsConstants';

export const usePromptTemplate = (templateId, verticalId = '', intent = '') => {
  const [record, setRecord] = useState(null);
  const [templatesByType, setTemplatesByType] = useState({
    system: { id: null, template: '' },
    user: { id: null, template: '' },
    assistant: { id: null, template: '' },
  });
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState('');

  const fetchVersions = useCallback(async (id) => {
    if (!id) {
      setVersions([]);
      return;
    }
    try {
      setLoadingVersions(true);
      const res = await fetch(`${SERVER_URL}/api/ui/bo/prompts/${id}/versions`, {
        method: 'GET',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setVersions(data?.list ?? []);
      } else {
        setVersions([]);
      }
    } catch (err) {
      console.error('PromptTemplate: Error loading versions:', err);
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  const loadPrompt = useCallback(async (id, vId = '', intentValue = '') => {
    try {
      if (id) {
        const base = await getRecord(ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE, id);
        if (!base || Object.keys(base).length === 0) {
          throw new Error('Prompt template not found');
        }
        
        const group = {
          operator: OPERATOR_AND,
          groups: [
            {
              operator: OPERATOR_AND,
              criteria: [
                {
                  fieldName: 'category',
                  operator: OPERATOR_EQUAL,
                  value: base.category,
                },
              ],
            },
            {
              operator: OPERATOR_AND,
              criteria: [
                {
                  fieldName: 'name',
                  operator: OPERATOR_EQUAL,
                  value: base.name,
                },
              ],
            },
          ],
        };

        const searchResult = await searchEntityRecords(
          ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE,
          { pageNumber: 1, pageSize: 100, group }
        );
        const list = searchResult?.data?.list ?? [];

        // The (category, name) group contains base rows (masterCompanyTypeId
        // AND intent null) plus one row-set per vertical/intent override.
        // Both dimensions are independent and nullable ("applies to all"),
        // so the target row-set is the exact (vertical, intent) match.
        const wantVertical = vId ? Number(vId) : null;
        const wantIntent = intentValue || null;
        const isScoped = Boolean(wantVertical || wantIntent);

        const baseRows = list.filter((row) => !row.masterCompanyTypeId && !row.intent);
        const targetRows = isScoped
          ? list.filter((row) => {
              const rowVertical = row.masterCompanyTypeId ? Number(row.masterCompanyTypeId) : null;
              const rowIntent = row.intent || null;
              return rowVertical === wantVertical && rowIntent === wantIntent;
            })
          : [];

        const nextTemplatesByType = {
          system: { id: null, template: '' },
          user: { id: null, template: '' },
          assistant: { id: null, template: '' },
        };

        // Prefill from base rows — when creating a new override the admin
        // starts from the base prompt (ids stay null so save inserts)
        baseRows.forEach((row) => {
          if (!row?.type || !nextTemplatesByType[row.type]) return;
          nextTemplatesByType[row.type] = {
            id: isScoped ? null : row.id,
            template: row.template ?? '',
          };
        });
        targetRows.forEach((row) => {
          if (!row?.type || !nextTemplatesByType[row.type]) return;
          nextTemplatesByType[row.type] = {
            id: row.id,
            template: row.template ?? '',
          };
        });

        // Primary id drives saveGroup targeting and version history — for a
        // scoped override it's the target system row (null until first save)
        // For the base view the primary id must be the BASE system row, not
        // the row from the URL — that row may be a scoped override, and
        // saveGroup derives the target vertical/intent from the primary id
        const basePrimaryId = baseRows.find((row) => row.type === 'system')?.id ?? null;
        const primaryId = isScoped
          ? (targetRows.find((row) => row.type === 'system')?.id ?? null)
          : basePrimaryId;

        setRecord({
          id: primaryId,
          basePrimaryId,
          category: base.category ?? '',
          name: base.name ?? '',
          variablesHelp: base.variablesHelp ?? '',
          masterCompanyTypeId: wantVertical,
          intent: wantIntent,
          hasVerticalOverride: targetRows.length > 0,
          // Vertical/intent of the row the editor was opened with — lets the
          // page preselect the right scope when a scoped row is opened from
          // the list (the row id in the URL may be a scoped override row)
          rowMasterCompanyTypeId: base.masterCompanyTypeId ?? null,
          rowIntent: base.intent ?? null,
        });
        setTemplatesByType(nextTemplatesByType);
        setSelectedVersionId('');

        await fetchVersions(primaryId);
      } else {
        const item = await getDefaultObject(ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE);
        setRecord({
          id: null,
          category: item.category ?? '',
          name: item.name ?? '',
          variablesHelp: item.variablesHelp ?? '',
        });
        setTemplatesByType({
          system: { id: null, template: '' },
          user: { id: null, template: '' },
          assistant: { id: null, template: '' },
        });
        setVersions([]);
        setSelectedVersionId('');
      }
    } catch (error) {
      console.error('PromptTemplate: Error loading record:', error);
      showErrorMessage('Failed to load prompt template.');
    } finally {
      setLoading(false);
    }
  }, [fetchVersions]);

  useEffect(() => {
    setLoading(true);
    loadPrompt(templateId, verticalId, intent);
  }, [templateId, verticalId, intent, loadPrompt]);

  const reload = useCallback(() => {
    setLoading(true);
    return loadPrompt(templateId, verticalId, intent);
  }, [templateId, verticalId, intent, loadPrompt]);

  const onFieldChange = useCallback((name, value) => {
    setRecord(prev => ({ ...prev, [name]: value }));
  }, []);

  const onTemplateChange = useCallback((type, value) => {
    setTemplatesByType(prev => ({
      ...prev,
      [type]: {
        ...(prev[type] || { id: null }),
        template: value,
      },
    }));
  }, []);

  const onVersionSelectChange = useCallback((event) => {
    const value = event.target.value;
    setSelectedVersionId(value);

    if (!value) return;

    const version = versions.find((v) => String(v.id) === value);
    if (!version) return;

    const snapshot = version.snapshot || {};
    const templateSnapshots = snapshot.prompts || {};
    const variablesHelpSnapshot = snapshot.variablesHelp ?? version.variablesHelp;

    setRecord((prev) => ({
      ...(prev || {}),
      category: snapshot.category ?? prev?.category ?? '',
      name: snapshot.name ?? prev?.name ?? '',
      variablesHelp: variablesHelpSnapshot ?? prev?.variablesHelp ?? '',
    }));

    setTemplatesByType((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((typeKey) => {
        const typeSnapshot = templateSnapshots[typeKey];
        next[typeKey] = {
          ...(next[typeKey] || { id: null }),
          template: typeSnapshot?.template ?? '',
        };
      });
      return next;
    });
  }, [versions]);

  return {
    record,
    templatesByType,
    loading,
    versions,
    loadingVersions,
    selectedVersionId,
    onFieldChange,
    onTemplateChange,
    onVersionSelectChange,
    reload,
  };
};
